import React from "react";
import { PlxsButton } from "@plexlogic/plexus-ui";
import { toSentenceCase } from "../../utils/stringUtils";
import "./WinnersTable.css";

interface WinnerResult {
  id: string;
  entry_number: number;
  prize_description: string;
  participant_identification?: string;
  csv_row_number?: number;  // Actual CSV row number
  is_reserve: boolean;
  selection_order: number;
  winner_type?: string;  // Pre-calculated winner type from backend
  certificate_generated: boolean;
  selected_at?: string;
}

interface WinnersTableProps {
  winners: WinnerResult[];  // Flat list of winners in correct order from backend
  onDownloadResults: () => void;
  entryMethod?: "simple" | "standard" | "advanced";  // How entries were provided
}

const WinnersTable: React.FC<WinnersTableProps> = ({
  winners,
  onDownloadResults,
  entryMethod = "simple",
}) => {
  if (winners.length === 0) {
    return null;
  }

  // List-based draws (CSV upload) identify winners by name and show the CSV row;
  // entry-count draws identify by entry number and have no CSV row.
  const isUploadDraw = entryMethod !== "simple";
  const identificationLabel = isUploadDraw ? "Winner identification" : "Winner ID";

  // Winners come from backend in correct order with winner_type already calculated
  // Just display them as-is!

  return (
    <div className="winners-section">
      <div className="draw-detail-card">
        <div className="winners-header">
          <h3>{toSentenceCase("Winners")}</h3>
          <PlxsButton
            dataTestId="winners-table-download-button"
            label="Download"
            variant="secondary"
            tone="neutral"
            size="sm"
            decorativeIcon="download"
            onClick={onDownloadResults}
          />
        </div>

        {/* Winners Table */}
        <div
          className={`winners-table${
            isUploadDraw ? " winners-table--with-csv-row" : ""
          }`}
        >
          {/* Table Header */}
          <div className="winners-table-header">
            <div className="winners-table-header-cell">
              {toSentenceCase("Winner no")}
            </div>
            {isUploadDraw && (
              <div className="winners-table-header-cell">
                {toSentenceCase("CSV row")}
              </div>
            )}
            <div className="winners-table-header-cell">
              {toSentenceCase(identificationLabel)}
            </div>
            <div className="winners-table-header-cell">
              {toSentenceCase("Prize description")}
            </div>
            <div className="winners-table-header-cell">
              {toSentenceCase("Winner type")}
            </div>
          </div>

          {/* Table Rows - already in correct order from backend */}
          {winners.map((winner, globalIndex) => {
            return (
              <div
                key={winner.id}
                className={`winners-table-row${
                  winner.is_reserve ? "" : " winners-table-row--winner"
                }`}
                style={{
                  borderBottom:
                    globalIndex < winners.length - 1
                      ? `var(--plexus-border-width-thin) solid var(--plexus-color-neutral-600)`
                      : "none",
                }}
              >
                <div className="winners-table-cell">
                  #{globalIndex + 1}
                </div>
                {isUploadDraw && (
                  <div className="winners-table-cell">
                    {winner.csv_row_number || "—"}
                  </div>
                )}
                <div className="winners-table-cell">
                  {winner.participant_identification ||
                    (winner.csv_row_number ? `Row ${winner.csv_row_number}` : `Entry ${winner.entry_number}`)}
                </div>
                <div className="winners-table-cell">
                  {winner.prize_description}
                </div>
                <div className="winners-table-cell">
                  {winner.winner_type}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default WinnersTable;
